import "../styles/TermsPrivacy.css";

const WEBSITE_URL = "https://www.kennethlpowell.com";
const CONTACT_EMAIL = "author@kennethlpowell.com";
const LAST_UPDATED = "2025-07-11";

export default function Terms() {
  return (
    <main className="legal-page-container">
      <header className="legal-page-header">
        <h1>Terms of Service</h1>

        <p className="legal-page-updated">
          Last updated:{" "}
          <time dateTime={LAST_UPDATED}>July 11, 2025</time>
        </p>
      </header>

      <section aria-labelledby="terms-acceptance">
        <h2 id="terms-acceptance">Acceptance of These Terms</h2>

        <p>
          These Terms of Service govern your access to and use of{" "}
          <a
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            kennethlpowell.com
          </a>
          , including its content, products, services, forms, and related
          features.
        </p>

        <p>
          By accessing or using this website, you agree to be bound by
          these Terms of Service and our Privacy Policy. If you do not
          agree with these terms, do not use the website.
        </p>
      </section>

      <section aria-labelledby="terms-eligibility">
        <h2 id="terms-eligibility">Eligibility</h2>

        <p>
          You must be legally capable of entering into a binding
          agreement to use this website or make a purchase. If you use
          the website on behalf of an organization, you represent that
          you have authority to bind that organization to these terms.
        </p>
      </section>

      <section aria-labelledby="terms-permitted-use">
        <h2 id="terms-permitted-use">Permitted Use</h2>

        <p>
          You may use this website for lawful, personal, and
          noncommercial purposes unless we provide written permission
          for another use.
        </p>

        <p>You agree not to:</p>

        <ul>
          <li>
            Use the website in violation of any applicable law or
            regulation.
          </li>

          <li>
            Attempt to gain unauthorized access to the website, its
            servers, accounts, databases, or administrative features.
          </li>

          <li>
            Interfere with the website&apos;s operation, security, or
            availability.
          </li>

          <li>
            Upload or transmit malware, harmful code, fraudulent
            material, or unlawful content.
          </li>

          <li>
            Scrape, copy, reproduce, or commercially exploit website
            content without permission.
          </li>

          <li>
            Impersonate another person or misrepresent your identity,
            affiliation, or authority.
          </li>
        </ul>
      </section>

      <section aria-labelledby="terms-intellectual-property">
        <h2 id="terms-intellectual-property">
          Intellectual Property
        </h2>

        <p>
          Unless otherwise stated, the website and its content are owned
          by or licensed to Kenneth L. Powell and Avwakin Publishing.
          This includes text, books, excerpts, artwork, graphics, logos,
          designs, characters, fictional settings, downloadable
          materials, and other creative content.
        </p>

        <p>
          You may not reproduce, distribute, modify, publish, sell,
          license, display, create derivative works from, or otherwise
          exploit protected content without prior written permission,
          except where permitted by applicable law.
        </p>
      </section>

      <section aria-labelledby="terms-user-submissions">
        <h2 id="terms-user-submissions">User Submissions</h2>

        <p>
          You may be able to send messages, images, testimonials,
          reviews, or other material through the website or by email.
          You retain ownership of material you submit.
        </p>

        <p>
          By submitting material for publication, promotion, or display,
          you grant us a nonexclusive, worldwide, royalty-free license
          to use, reproduce, display, edit, and distribute that material
          for the purpose for which it was submitted.
        </p>

        <p>
          You represent that you own or have permission to submit the
          material and that its use will not violate another
          person&apos;s rights.
        </p>
      </section>

      <section aria-labelledby="terms-products">
        <h2 id="terms-products">Products and Availability</h2>

        <p>
          We may offer books, merchandise, digital content, or other
          products through the website. Product descriptions, images,
          pricing, availability, and other details may change without
          notice.
        </p>

        <p>
          We make reasonable efforts to display products accurately, but
          colors, dimensions, formatting, and appearance may vary
          depending on your device, product format, printer,
          manufacturer, or fulfillment provider.
        </p>

        <p>
          We reserve the right to limit quantities, discontinue
          products, reject orders, or correct pricing and listing errors.
        </p>
      </section>

      <section aria-labelledby="terms-orders">
        <h2 id="terms-orders">Orders and Payment</h2>

        <p>
          By placing an order, you represent that the billing,
          shipping, and payment information you provide is accurate and
          that you are authorized to use the selected payment method.
        </p>

        <p>
          An order confirmation does not guarantee acceptance of an
          order. We may cancel or decline an order because of product
          availability, suspected fraud, payment problems, incorrect
          pricing, fulfillment limitations, or other reasonable
          concerns.
        </p>

        <p>
          Payments may be processed by third-party payment providers.
          Your use of their services may also be governed by their terms
          and privacy policies.
        </p>
      </section>

      <section aria-labelledby="terms-shipping">
        <h2 id="terms-shipping">
          Shipping, Fulfillment, and Delivery
        </h2>

        <p>
          Physical products may be produced, shipped, or fulfilled by
          third-party providers. Shipping and delivery estimates are not
          guaranteed and may be affected by production schedules,
          carrier delays, weather, customs, incorrect addresses, or
          other circumstances outside our control.
        </p>

        <p>
          You are responsible for providing a complete and accurate
          delivery address. Additional charges may apply when an order
          must be replaced or reshipped because of incorrect information
          supplied during checkout.
        </p>
      </section>

      <section aria-labelledby="terms-digital-products">
        <h2 id="terms-digital-products">Digital Products</h2>

        <p>
          Digital products are licensed for your personal use and are
          not sold with ownership of the underlying intellectual
          property.
        </p>

        <p>
          Unless expressly permitted, you may not share, resell,
          redistribute, upload, sublicense, or make purchased digital
          files available to other people or through file-sharing
          services.
        </p>
      </section>

      <section aria-labelledby="terms-returns">
        <h2 id="terms-returns">
          Returns, Refunds, and Replacements
        </h2>

        <p>
          Return, refund, and replacement eligibility may depend on the
          product type, condition, fulfillment provider, and reason for
          the request.
        </p>

        <p>
          Digital products may not be refundable after delivery or
          download except where required by law. Personalized,
          made-to-order, print-on-demand, or used products may also have
          limited return eligibility.
        </p>

        <p>
          Contact us promptly if an item arrives damaged, defective,
          incorrect, or incomplete. We may request photographs, order
          information, or other evidence before approving a replacement
          or refund.
        </p>
      </section>

      <section aria-labelledby="terms-accounts">
        <h2 id="terms-accounts">Accounts and Security</h2>

        <p>
          Certain areas of the website may require an account or login.
          You are responsible for safeguarding your credentials and for
          activity performed through your account.
        </p>

        <p>
          Notify us promptly if you believe your account or credentials
          have been accessed without authorization.
        </p>
      </section>

      <section aria-labelledby="terms-third-party-links">
        <h2 id="terms-third-party-links">
          Third-Party Websites and Services
        </h2>

        <p>
          The website may link to retailers, social media platforms,
          payment providers, fulfillment services, or other third-party
          websites.
        </p>

        <p>
          We do not control and are not responsible for third-party
          content, products, security, availability, policies, or
          practices. Your use of a third-party service is subject to its
          own terms and policies.
        </p>
      </section>

      <section aria-labelledby="terms-disclaimer">
        <h2 id="terms-disclaimer">Disclaimer of Warranties</h2>

        <p>
          To the fullest extent permitted by law, the website and its
          content are provided on an &quot;as is&quot; and &quot;as
          available&quot; basis without warranties of any kind, whether
          express or implied.
        </p>

        <p>
          We do not guarantee that the website will always be available,
          uninterrupted, secure, accurate, complete, or free from
          errors, viruses, or other harmful components.
        </p>
      </section>

      <section aria-labelledby="terms-limitation">
        <h2 id="terms-limitation">Limitation of Liability</h2>

        <p>
          To the fullest extent permitted by law, Kenneth L. Powell,
          Avwakin Publishing, and their service providers will not be
          liable for indirect, incidental, special, consequential,
          exemplary, or punitive damages arising from your use of or
          inability to use the website, products, or services.
        </p>

        <p>
          Some jurisdictions do not allow certain warranty exclusions or
          liability limitations. In those jurisdictions, these
          provisions apply only to the extent permitted by law.
        </p>
      </section>

      <section aria-labelledby="terms-indemnification">
        <h2 id="terms-indemnification">Indemnification</h2>

        <p>
          To the extent permitted by law, you agree to indemnify and
          hold harmless Kenneth L. Powell and Avwakin Publishing from
          claims, damages, losses, liabilities, and reasonable expenses
          arising from your misuse of the website, violation of these
          terms, or infringement of another person&apos;s rights.
        </p>
      </section>

      <section aria-labelledby="terms-termination">
        <h2 id="terms-termination">Suspension and Termination</h2>

        <p>
          We may restrict, suspend, or terminate access to the website or
          its features when we reasonably believe a user has violated
          these terms, created a security risk, engaged in fraud, or
          harmed the website or another person.
        </p>
      </section>

      <section aria-labelledby="terms-governing-law">
        <h2 id="terms-governing-law">
          Governing Law and Disputes
        </h2>

        <p>
          These terms will be governed by the applicable laws of the
          jurisdiction in which the website operator is established,
          without regard to conflict-of-law principles.
        </p>

        <p>
          Before beginning formal legal proceedings, you agree to make a
          reasonable effort to contact us and attempt to resolve the
          dispute informally.
        </p>
      </section>

      <section aria-labelledby="terms-changes">
        <h2 id="terms-changes">Changes to These Terms</h2>

        <p>
          We may revise these Terms of Service from time to time.
          Updated terms will be posted on this page with a revised
          effective date.
        </p>

        <p>
          Your continued use of the website after updated terms become
          effective constitutes acceptance of those changes.
        </p>
      </section>

      <section aria-labelledby="terms-severability">
        <h2 id="terms-severability">Severability</h2>

        <p>
          If any provision of these terms is found unenforceable, the
          remaining provisions will continue in effect. The
          unenforceable provision will be limited or modified only as
          much as necessary to make it enforceable.
        </p>
      </section>

      <section aria-labelledby="terms-entire-agreement">
        <h2 id="terms-entire-agreement">Entire Agreement</h2>

        <p>
          These Terms of Service and the Privacy Policy constitute the
          entire agreement between you and the website operator
          concerning your use of the website, except where separate
          written terms apply to a specific product or service.
        </p>
      </section>

      <section aria-labelledby="terms-contact">
        <h2 id="terms-contact">Contact Us</h2>

        <p>
          Contact us with questions about these Terms of Service:
        </p>

        <address className="legal-contact-information">
          <p>
            <strong>Email:</strong>{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>

          <p>
            <strong>Website:</strong>{" "}
            <a
              href={WEBSITE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              kennethlpowell.com
            </a>
          </p>
        </address>
      </section>
    </main>
  );
}